<template>
  <Card
    :color="project.color"
    :entity="project"
    :link-to="linkTo"
    show-settings
    type="project"
    @card-click="onCardClick(project)"
    v-if="user"
    data-test-id="project-card"
    :data-test-name="project.name"
    :data-test-type="project.type"
    :data-test-description="project.description"
    :data-test-owner="project.owner.fullname"
    :data-test-team="[
      project.owner.fullname,
      ...project.activeTeamMembers.map((member) => member.user.fullname || $t('deletedUser')),
    ]"
  >
    <template #top>
      <div v-if="project.type === 'joined' || project.type === 'invited'" class="project-card__type">
        <div class="type-badge">
          <div class="type-badge__text">
            {{ cardBadgeText() }}
          </div>
        </div>
      </div>

      <div class="project-card__name" :title="project.name">
        {{ project.name }}
      </div>

      <div class="project-card__description">
        {{ truncate(project.description, 140) }}
      </div>
    </template>
    <template #bottom>
      <div class="project-card__team">
        <template v-if="project.type === 'invited'">
          <UserAvatar
            :image="project.owner.image"
            :firstname="project.owner.firstname"
            :lastname="project.owner.lastname"
          />
        </template>
        <template v-else>
          <MemberAvatarList data-test-id="project-card-team-list" :user="user" :members="members" />
        </template>
      </div>

      <div class="project-card__actions" v-if="project.type === 'invited'">
        <div class="project-card__action">
          <ButtonLoading @click="acceptInvite(project)" :status="inviteStatus" :is-disabled="rejectStatus > 0">
            {{ $t('projectCard.acceptInvitation') }}
          </ButtonLoading>
        </div>

        <div class="project-card__action">
          <ButtonLoading
            @click="rejectInvite(project)"
            :status="rejectStatus"
            :is-disabled="inviteStatus > 0"
            color="grey"
          >
            {{ $t('projectCard.rejectInvitation') }}
          </ButtonLoading>
        </div>
      </div>
    </template>
    <template #settings>
      <Btn
        v-if="project.owner.id === user.id"
        @click="showTeamInvitation = true"
        icon
        tabindex="-1"
        color="ghost"
        data-test-id="invitation-add"
      >
        <AddUserIcon16 />
      </Btn>

      <ProjectCardMenu
        v-if="project.type !== 'invited'"
        :project="project"
        :user="user"
        @show-invite-menu="showTeamInvitation = true"
        @show-delete-menu="showProjectDelete = true"
      />
    </template>
  </Card>
  <PortalModal :open="showTeamInvitation" @cancel="showTeamInvitation = false" data-test-id="modal-team-invitation">
    <TeamInvitationOverlay :user="user" :project="project" @cancel="showTeamInvitation = false" />
  </PortalModal>
  <PortalModal :open="showProjectDelete" @cancel="showProjectDelete = false" data-test-id="modal-project-delete">
    <DeleteProjectCard :project="project" @cancel="showProjectDelete = false" />
  </PortalModal>
</template>

<script lang="ts">
import { defineComponent, defineAsyncComponent, PropType } from 'vue';
import { VueApolloQueryDefinition } from '@vue/apollo-option/types/options';
import MemberAvatarList from '../MemberAvatarList.vue';
import AddUserIcon16 from '@/components/Icons/AddUserIcon16.vue';
import { AcceptProjectInviteLookup, UserQuery, UserQueryVariables, UserDocument } from '@/graphql/operations.gen';
import { ProjectWithType } from './types';

import { acceptInviteToProject, rejectInviteToProject, getSuccessNotification } from '@utils/projectInvite';
import ProjectCardMenu from '@components/ProjectOverview/ProjectCardMenu.vue';
import DeleteProjectCard from '@components/DeleteProjectCard.vue';
import Card from '@components/Card/Card.vue';
import PortalModal from '@components/PortalModal.vue';

const TeamInvitationOverlay = defineAsyncComponent(
  () => import(/* webpackChunkName: "projectModals" */ '@components/Team/Invitation/TeamInvitationOverlay.vue')
);

export default defineComponent({
  name: 'ProjectCard',

  components: {
    Card,
    PortalModal,
    AddUserIcon16,
    MemberAvatarList,
    TeamInvitationOverlay,
    ProjectCardMenu,
    DeleteProjectCard,
  },

  props: {
    project: {
      type: Object as PropType<ProjectWithType>,
      required: true,
    },

    user: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      showTeamInvitation: false,
      showProjectDelete: false,
      inviteStatus: 0,
      rejectStatus: 0,
    };
  },

  apollo: {
    user(): VueApolloQueryDefinition<UserQuery, UserQueryVariables> {
      return {
        query: UserDocument,
        fetchPolicy: 'cache-first',
      };
    },
  },

  computed: {
    hasLink(): boolean {
      return this.project.type !== 'invited';
    },

    linkTo(): object | undefined {
      return this.hasLink ? { name: 'sources', params: { projectId: this.project.id } } : {};
    },

    members(): Array<any> {
      return [{ user: this.project.owner }, ...this.project.activeTeamMembers];
    },
  },

  methods: {
    async acceptInvite(project: ProjectWithType) {
      this.inviteStatus = 2;

      // Define the success notification here, because a successful invite removes the card which destroys the references
      // to "this"
      const successNotification = getSuccessNotification(project.id);

      await acceptInviteToProject({
        projectId: project.id,
        lookupType: AcceptProjectInviteLookup.Id,
        inviteToken: null,
      }).finally(() => {
        // Resets the loading indicator at the end of execution
        this.inviteStatus = 0;
      });

      this.$store.dispatch('notification/add', successNotification);
    },

    async rejectInvite(project: ProjectWithType) {
      this.rejectStatus = 2;

      // Define the success notification here, because a successful invite removes the card which destroys the references
      // to "this"
      const successNotification = {
        message: this.$t('team.invitation.rejected'),
      };

      await rejectInviteToProject({
        id: project.id,
      }).finally(() => {
        // Resets the loading indicator at the end of execution
        this.rejectStatus = 0;
      });

      this.$store.dispatch('notification/add', successNotification);
    },

    onCardClick(project: ProjectWithType) {
      if (
        project.name === 'Demo Interview Project' &&
        project.description ===
          'This is an example project to showcase ATLAS.ti Cloud. It is only partly coded. Practice coding and your coding skills by trying to apply existing codes to not yet coded data segments or create new codes.'
      ) {
        this.$tracker.trackDemoProjectOpened();
      }
    },

    truncate(str: string, len: number) {
      return str.length > len ? `${str.substring(0, len - 1)}…` : str;
    },

    cardBadgeText() {
      if (this.project.type === 'invited') {
        return this.$t('projectCard.projectInviteBy', {
          name: `${this.project.owner.firstname} ${this.project.owner.lastname}`,
        });
      } else if (this.project.type === 'joined') {
        return this.$t('projectCard.invitedBy', { name: this.project.owner.firstname });
      }

      return '';
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'mixins/typography';
.project-card__name {
  color: colors.$color-grey-900;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @include typography.typography-14-medium(160%);
}

.project-card__description {
  color: colors.$color-grey-500;
  overflow-wrap: break-word;
  word-wrap: break-word;
  -webkit-hyphens: auto;
  -ms-hyphens: auto;
  -moz-hyphens: auto;
  hyphens: auto;
  max-height: 66px;

  @include typography.typography-14-normal(160%);
}

.project-card__team {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.project-card__actions {
  display: flex;
  margin-left: auto;
  gap: 4px;
}

.project-card__type {
  padding: 4px;
  width: fit-content;
  border-radius: 4px;
  background: colors.$color-grey-100;

  .type-badge__text {
    color: colors.$color-grey-500;

    @include typography.typography-13-normal;
  }
}
</style>
